import { ResendCode } from "@faire/retailer-visitor-shared/components/AppSignIn/components/__internal__/ResendCode";
import { ContinueToNextViewButton } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ContinueToNextViewButton";
import { convertErrorToLoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { LoginError } from "@faire/retailer-visitor-shared/components/AppSignIn/components/LoginError";
import { LoginWrapper } from "@faire/retailer-visitor-shared/components/AppSignIn/components/styled";
import {
  isNumericOrBlank,
  isNumericOrBlankCode,
  VALID_CODE_LENGTH,
} from "@faire/retailer-visitor-shared/components/AppSignIn/components/VerificationInputUtils";
import { View } from "@faire/retailer-visitor-shared/components/AppSignIn/components/View";
import { useSignInEventTracker } from "@faire/retailer-visitor-shared/components/AppSignIn/useSignInEventTracker";
import { VerificationInput } from "@faire/retailer-visitor-shared/components/VerificationInput/VerificationInput";
import login from "@faire/web-api--source/endpoints/www/api/v2/users/login/post";
import { trackRetailerPortalLoginModalLoggedInWithCode } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalLoggedInWithCode";
import { trackRetailerPortalLoginModalLoginWithCode } from "@faire/web-api--source/events/retailerPortal/actionCategoryOther/loginModalLoginWithCode";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, useRef, useState } from "react";

type EnterCodeResult = {
  nextView: View;
  loggedInUser: IUser;
};

interface IProps {
  email: string;
  userToken?: string;
  onComplete: (result: EnterCodeResult) => void;
  onLogin: (user: IUser) => void;
}

export const EnterCodeView: FC<IProps> = ({
  email,
  userToken,
  onComplete,
  onLogin,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [showResend, setShowResend] = useState(true);
  const [isLoadingAutoLogin, setIsLoadingAutoLogin] = useState(false);
  const code = useRef("");
  const [error, setError] = useState<LoginError>();
  const [codeInputError, setCodeInputError] = useState(false);
  const [verificationInputErrorMessage, setVerificationInputErrorMessage] =
    useState<string>();
  const { trackFieldValue } = useSignInEventTracker("ENTER_CODE");

  const onContinue = async () => {
    trackRetailerPortalLoginModalLoginWithCode();
    setError(undefined);
    setIsLoadingAutoLogin(true);

    try {
      const { user } = await login(
        ILoginRequest.build({
          code: code.current,
          email_address: email,
          user_token: !email ? userToken : undefined,
        })
      );

      if (!user) {
        setError(
          new LoginError(
            strictLocalize({
              id: "bw8Bnw",
              defaultMessage: "Invalid credentials",
              description: {
                text: "Email or password that was entered is incorrect",
              },
            })
          )
        );
        return;
      }

      const nextView = user.missing_password
        ? View.CREATE_PASSWORD
        : View.LOADING;
      if (nextView === View.LOADING) {
        trackRetailerPortalLoginModalLoggedInWithCode();
        onLogin(user);
      }
      onComplete({ nextView, loggedInUser: user });
    } catch (e) {
      setCodeInputError(true);
      setVerificationInputErrorMessage(
        strictLocalize({
          id: "2p8Fco",
          defaultMessage: "Incorrect code",
          description: {
            text: "Verifiction code that was entered is incorrect",
            img: "https://cdn.faire.com/fastly/500b65aba2a2d3e9acdba9fe58808512d08a58f024d8d9de361c75ec226f0d1a.png",
          },
        })
      );
    } finally {
      setIsLoadingAutoLogin(false);
    }
  };

  const isValidCode = (): boolean =>
    code.current.length === VALID_CODE_LENGTH &&
    !verificationInputErrorMessage &&
    !codeInputError;

  const handleVerificationCodeInputChange = (
    codeValue: string,
    slotValue: string
  ) => {
    code.current = codeValue;
    setCodeInputError(false);
    trackFieldValue("verificationCode", codeValue);

    if (!isNumericOrBlankCode(slotValue)) {
      setVerificationInputErrorMessage(
        strictLocalize({
          id: "Wy3Kfu",
          defaultMessage: "Enter numbers only.",
          description: {
            text: "Email or password that was entered is incorrect",
            img: "https://cdn.faire.com/fastly/52a1bf8c774d8b3fed237efa2a26b2870aeac3cf261e66a7694e6083f7a0398e.png",
          },
        })
      );
    } else if (isNumericOrBlankCode(code.current)) {
      setVerificationInputErrorMessage(undefined);
      if (isValidCode()) {
        onContinue();
      }
    }
  };

  const onResend = async () => {
    try {
      await login(
        ILoginRequest.build({
          email_address: email,
          user_token: !email ? userToken : undefined,
          resend: true,
        })
      );
      setShowResend(false);
      setError(undefined);
    } catch (e) {
      setError(convertErrorToLoginError(e, strictLocalize));
    }
  };

  return (
    <LoginWrapper>
      <Typography variant="displaySSerifRegular">
        <StrictLocalMsg
          id="7wIj4L"
          defaultMessage="Check your email"
          description={{
            text: "User is almost at the end of the log in process, enter the verification code send to their email",
            img: "https://cdn.faire.com/fastly/1646246e95e555c20bd5350ce71cbda26bc9997cadc706a2894f15e51bd62f5d.png",
          }}
        />
      </Typography>
      <Spacer height="2x" />
      <Typography>
        <StrictLocalMsg
          id="/9/chi"
          defaultMessage="Click the link we sent to your email {userEmailAddress} or enter the 6-digit code to sign in without a password."
          description={{
            text: "This refers to the recovery code we send to users who lost/forgot their password",
            img: "https://cdn.faire.com/fastly/1646246e95e555c20bd5350ce71cbda26bc9997cadc706a2894f15e51bd62f5d.png",
          }}
          values={{
            userEmailAddress: email,
          }}
        />
      </Typography>
      <Spacer height="3x" />
      <VerificationInput
        validateFn={isNumericOrBlank}
        onChange={handleVerificationCodeInputChange}
        errorMessage={verificationInputErrorMessage ?? error?.message}
        errorAll={codeInputError}
        isValidInput={isValidCode()}
        value={code.current}
        ariaLabel={strictLocalize({
          id: "tkYzhg",
          defaultMessage: "Enter Emailed Code",
          description: {
            text: "Accessible name for prompt for user to enter a code that was sent to their email",
          },
        })}
      />
      <Spacer height="3x" />
      <ResendCode error={error} onResend={onResend} showResend={showResend} />
      <Spacer height="3x" />
      <ContinueToNextViewButton
        onClick={onContinue}
        isLoading={isLoadingAutoLogin}
        disabled={!isValidCode()}
      />
    </LoginWrapper>
  );
};
